/**
 * The player-count editor's body — shared by the desktop modal and the phone
 * sheet, because the two differ in chrome and in nothing else.
 *
 * IT WRITES A GLOBAL VALUE. Not crew-scoped: "a maximum player count is a fact
 * about the game" (`DISCOVERY.md` §5.10), decided against the design, which
 * says `SET BY THE CREW` in four places. Those four strings are false here and
 * are not ported; the footnote below says plainly what pressing the button does.
 *
 * THE BASELINE NOTE IS THE WHOLE OF CONFLICT C3 and lives in
 * `format.ts#playersSrcTxt` — the design's `STEAM SAYS 1-4` family names a
 * source that publishes category flags and no numeric maximum, and the obvious
 * replacement invents a minimum for two sources that publish only a ceiling.
 *
 * WHAT MAKES A GLOBAL WRITE SURVIVABLE IS THE ATTRIBUTION, and it ships here
 * rather than later: every override says who set it and when, and the reset is
 * one press away. Community databases that hold up (OSM, Discogs) do not prevent
 * bad edits — they make them visible and cheap to undo. Note the limit honestly:
 * this reverts to THE SOURCE COUNT, not to whatever the previous override was.
 * The earlier value exists only in an admin-readable audit table.
 */

import { ago, playersSrcTxt } from '../lib/format.ts';
import { useQd } from '../store/index.ts';
import { PixelButton } from './PixelButton.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

const LABEL: CSSProperties = {
  fontFamily: MONO,
  fontSize: 8.5,
  letterSpacing: '.11em',
  color: 'var(--gt-muted)',
};

/** `phone` widens the touch targets and stacks the reset under the CTA. */
export function PlayerCountEditor({ phone }: { phone?: boolean }): JSX.Element | null {
  const s = useQd();
  const appid = s.capOpen;
  const g = s.gById(appid);
  if (!appid || !g) return null;

  const o = s.ovr[appid] ?? null;
  const baseline = playersSrcTxt(g);
  const me = s.me();

  // Who set it. Three states beyond "you", and they are genuinely different:
  // the override is GLOBAL while the roster is crew-scoped, so the setter may
  // be someone this member cannot be shown — and a deleted profile is not the
  // same as a stranger.
  const byTxt = !o
    ? ''
    : me && o.byKey === me.k
      ? 'SET BY YOU · ' + ago(o.at, Date.now())
      : o.byKey
        ? 'SET BY ' + o.byKey.toUpperCase() + ' · ' + ago(o.at, Date.now())
        : o.byKnown
          ? 'SET BY SOMEONE OUTSIDE YOUR PARTY'
          : 'SET BY A FORMER MEMBER';

  const input: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--gt-field)',
    border: '1px solid var(--gt-border-2)',
    color: 'var(--gt-text)',
    fontFamily: MONO,
    fontSize: phone ? 16 : 14,
    // 16px on phone is not a style choice: iOS Safari zooms the viewport on
    // focus for anything smaller, and the zoom does not come back.
    height: phone ? 48 : 40,
    padding: '0 12px',
    outline: 'none',
    textAlign: 'center',
  };

  return (
    // THE BODY OWNS ITS OWN INSET. Neither chrome supplies one: `Modal` and
    // `Sheet` both render `{children}` straight after their header row, so a
    // body that pads to 0 sits flush against the panel border while the header
    // above it is inset — which is what shipped. The horizontal value matches
    // the header it sits under (`ModalHeader` 18px, `Sheet` 14px) so the title
    // and the first label share a left edge; the extra bottom is the house
    // shape every other body uses (`PtAddModal`, `PtSyncModal`, `FiltersSheet`).
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: phone ? '14px 14px 18px' : '16px 18px 18px',
      }}
    >
      {/* Only shown when there is a source to name. An override IS the current
          value, so this slot yields to the attribution line below. */}
      {baseline ? <div style={LABEL}>{baseline}</div> : null}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={LABEL}>MIN</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={1000}
            value={s.capMn}
            onChange={(e) => s.setCap({ mn: e.target.value })}
            style={input}
          />
        </label>
        <span style={{ fontFamily: MONO, fontSize: 13, color: 'var(--gt-muted-2)', paddingBottom: phone ? 14 : 10 }}>
          —
        </span>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={LABEL}>MAX</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={1000}
            value={s.capMx}
            onChange={(e) => s.setCap({ mx: e.target.value })}
            // Enter submits, like every other single-purpose form in the app.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.repeat) void s.saveCap();
            }}
            style={input}
          />
        </label>
      </div>

      {byTxt ? (
        <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.05em', color: 'var(--gt-text-5)' }}>
          CURRENT OVERRIDE: <span style={{ color: 'var(--gt-text-3)' }}>{byTxt}</span>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexDirection: phone ? 'column' : 'row',
          alignItems: phone ? 'stretch' : 'center',
          gap: phone ? 10 : 12,
        }}
      >
        {/* Arms only over an EXISTING override, which is the press that cannot
            be undone — the number it replaces survives only in an audit table
            no member can read. A first set is recoverable through RESET, so it
            commits on one press rather than training the confirmation away. */}
        <PixelButton
          label={
            s.capBusy ? 'SAVING…' : s.capSetArm ? 'SURE? — REPLACE IT FOR EVERYONE' : 'SET FOR EVERYONE ▸'
          }
          busy={s.capBusy}
          onClick={() => void s.saveCap()}
          fs={8.5}
          pad={phone ? '17px 14px' : '14px 16px'}
          center={!!phone}
          nowrap={!phone}
        />
        {/* Only offered when there is something to revert. Arms first: it is a
            crew-wide change like the write, and the number it drops back to is
            not the one the previous editor chose. */}
        {o ? (
          <span
            onClick={() => {
              if (s.capBusy) return;
              if (!s.capArm) {
                s.setCapArm(true);
                return;
              }
              void s.clearCap();
            }}
            data-tip={s.capArm ? 'PRESS AGAIN TO REVERT IT FOR EVERYONE' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: '.06em',
              color: s.capArm ? 'var(--acc2)' : 'var(--gt-text-5)',
              border: '1px solid ' + (s.capArm ? 'var(--acc2)' : 'var(--gt-border-2)'),
              padding: phone ? '14px 12px' : '12px 12px',
              whiteSpace: phone ? 'normal' : 'nowrap',
              cursor: s.capBusy ? 'progress' : 'pointer',
              textAlign: 'center',
            }}
          >
            {s.capBusy ? 'SAVING…' : s.capArm ? 'SURE? — BACK TO THE SOURCE COUNT' : 'RESET'}
          </span>
        ) : null}
      </div>

      <div
        style={{
          border: '1px dashed var(--gt-border-3)',
          padding: '10px 11px',
          fontFamily: MONO,
          fontSize: 8.5,
          lineHeight: 1.8,
          letterSpacing: '.05em',
          color: 'var(--gt-muted-2)',
        }}
      >
        GLOBAL — THIS CHANGES THE COUNT FOR EVERY PARTY · THE SOURCE COUNT STAYS ON RECORD · EVERY
        CHANGE IS SIGNED
      </div>
    </div>
  );
}
